/**
 * Diagnostico Nuvem/App Service - N226
 *
 * Tempo de cada requisicao no servidor (middleware, `fato_requisicao_api`) e de
 * cada sincronizacao que o app reporta (`fato_sync_execucao`). Nasceu do N225:
 * producao saturava e ninguem via (Caddy, nginx e gunicorn nao registram duracao).
 * Leitura MASTER-only no backend (403 para o resto); o guard da tela e cortesia.
 */
import apiClient from './apiClient';
import { MESSAGES } from '../utils/messages';

export type Origem = 'app' | 'web' | 'outro';

export interface ResumoKpis {
  requisicoes: number;
  p50_ms: number | null;
  p95_ms: number | null;
  max_ms: number | null;
  pico_simultaneas: number;
  db_ms_total: number;
  cpu_ms_total: number;
  sincronizacoes: number;
  sync_p50_ms: number | null;
  sync_p95_ms: number | null;
}

export interface ResumoHora {
  hora: string;
  requisicoes: number;
  app: number;
  web: number;
  outro: number;
  p50_ms: number | null;
  p95_ms: number | null;
  max_ms: number | null;
  simultaneas_max: number;
  db_ms: number;
  cpu_ms: number;
}

export interface ResumoRota {
  metodo: string;
  rota: string;
  origem: Origem;
  n: number;
  total_ms: number;
  p50_ms: number;
  p95_ms: number;
  max_ms: number;
  db_ms_medio: number;
  db_consultas_medio: number;
  cpu_ms_medio: number;
}

export interface ResumoNuvem {
  inicio: string;
  fim: string;
  kpis: ResumoKpis;
  por_hora: ResumoHora[];
  rotas: ResumoRota[];
}

/** Vem do JSON do app: so `nome` e `ms` sao garantidos. */
export interface EtapaSync {
  nome: string;
  ms: number;
  requisicoes?: number;
  itens?: number;
  retentativas?: number;
  erros?: number;
}

export interface SincronizacaoItem {
  sync_id: string;
  usuario_id: number | null;
  usuario_nome: string | null;
  device_name: string;
  app_version: string;
  plataforma: string;
  gatilho: string;
  tipo: string;
  iniciado_em: string;
  finalizado_em: string;
  duracao_ms: number;
  sucesso: boolean;
  erro: string;
  requisicoes: number;
  retentativas: number;
  servidor_ms: number;
  rede_app_ms: number;
  requisicoes_servidor: number;
  etapa_mais_lenta: { nome: string; ms: number } | null;
}

export interface SincronizacaoListResponse {
  items: SincronizacaoItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface RequisicaoSync {
  criado_em: string;
  metodo: string;
  rota: string;
  status: number;
  duracao_ms: number;
  db_ms: number;
  db_consultas: number;
  cpu_ms: number;
  bytes_resposta: number;
}

export interface RotaSync {
  metodo: string;
  rota: string;
  n: number;
  total_ms: number;
  p50_ms: number;
  max_ms: number;
}

export interface SincronizacaoDetalhe {
  execucao: SincronizacaoItem | null;
  etapas: EtapaSync[];
  truncado: boolean;
  requisicoes: RequisicaoSync[];
  rotas: RotaSync[];
}

/** Cada bloco e null quando o servidor nao consegue ler (Windows/SQLite, /proc ausente). */
export interface ServidorNuvem {
  ambiente: string;
  agora: string;
  pid: number;
  cpu_count: number | null;
  load_avg: number[] | null;
  memoria: { total_mb: number; disponivel_mb: number } | null;
  gunicorn: { workers: number; threads: number } | null;
  banco: {
    vendor: string;
    tamanho: string | null;
    conexoes: number | null;
    max_connections: number | null;
  } | null;
}

export interface FiltrosSincronizacao {
  inicio?: string;
  fim?: string;
  usuario_id?: number;
  device_name?: string;
  app_version?: string;
  page?: number;
  page_size?: number;
}

export const SEM_DADOS = 'Sem dados no período';
/** Valor ausente na tela. Zero e medida; '—' e ausencia dela. */
export const SEM_VALOR = '—';

// Filtro vazio vira parametro ausente: `origem=` chegaria ao backend como valor invalido.
const semVazios = (params: object) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  );

export const obterResumo = async (filtros: {
  inicio?: string;
  fim?: string;
  origem?: Origem | '';
}): Promise<ResumoNuvem> => {
  const { data } = await apiClient.get<ResumoNuvem>('/diagnostico-nuvem/resumo', {
    params: semVazios(filtros),
  });
  return data;
};

export const listarSincronizacoes = async (
  filtros: FiltrosSincronizacao,
): Promise<SincronizacaoListResponse> => {
  const { data } = await apiClient.get<SincronizacaoListResponse>(
    '/diagnostico-nuvem/sincronizacoes',
    { params: semVazios(filtros) },
  );
  return data;
};

/** sync_id e texto livre do app (ate 64): codificado para nao virar outro caminho. */
export const obterSincronizacao = async (syncId: string): Promise<SincronizacaoDetalhe> => {
  const { data } = await apiClient.get<SincronizacaoDetalhe>(
    `/diagnostico-nuvem/sincronizacoes/${encodeURIComponent(syncId)}`,
  );
  return data;
};

export const obterServidor = async (): Promise<ServidorNuvem> => {
  const { data } = await apiClient.get<ServidorNuvem>('/diagnostico-nuvem/servidor');
  return data;
};

/** `detail` de HttpError e texto; o 422 do Ninja manda lista, e objeto no JSX derruba a tela. */
export const mensagemDeErro = (e: any): string => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : MESSAGES.GENERIC.ERROR;
};

/** Capacidade do gunicorn: requisicoes atendidas ao mesmo tempo (workers x threads). */
export const lugaresDoServidor = (servidor: ServidorNuvem | null): number | null => {
  const g = servidor?.gunicorn;
  return g && g.workers > 0 && g.threads > 0 ? g.workers * g.threads : null;
};

/** ms legivel: '60 ms', '1,3 s', '45 s', '2 min 05 s', '1 h 05 min'. */
export const formatarMs = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return SEM_VALOR;
  if (Math.round(ms) < 1000) return `${Math.round(ms)} ms`;
  // arredonda ANTES de escolher a unidade: 9.960 ms e '10 s', nao '10,0 s'
  const decimos = Math.round(ms / 100);
  if (decimos < 100) return `${(decimos / 10).toFixed(1).replace('.', ',')} s`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const dois = (n: number) => String(n).padStart(2, '0');
  if (s < 3600) return `${Math.floor(s / 60)} min ${dois(s % 60)} s`;
  return `${Math.floor(s / 3600)} h ${dois(Math.floor((s % 3600) / 60))} min`;
};

/**
 * Sem amostra o backend manda 0 nos percentis (nearest-rank de lista vazia) e no
 * servidor da sync: com `amostras` 0 o tempo e '—', nao "0 ms".
 */
export const formatarMsSeHouver = (ms: number | null | undefined, amostras: number): string =>
  formatarMs(amostras > 0 ? ms : null);

const MAX_DIAS = 31; // o mesmo `_MAX_DIAS` de api_diagnostico_nuvem.py

/**
 * As regras de periodo do backend (`_periodo`), conferidas antes de chamar: periodo
 * invalido nem vai a API. `fim` inclusivo. null = valido.
 */
export const validarPeriodo = (inicio: string, fim: string): string | null => {
  // campo apagado: vazia, o semVazios tiraria a data do pedido e o backend usaria o default
  if (!inicio || !fim) return 'Informe as duas datas.';
  // 'YYYY-MM-DD' o Date.parse le em UTC: a conta de dias nao depende do fuso
  const dias = (Date.parse(fim) - Date.parse(inicio)) / 86_400_000 + 1;
  if (Number.isNaN(dias)) return 'Data inválida.';
  if (dias < 1) return 'A data final não pode ser anterior à inicial.';
  if (dias > MAX_DIAS) return `Período máximo de ${MAX_DIAS} dias.`;
  return null;
};

// Datas sempre no fuso de Sao Paulo, nao no do PC de quem abre a tela.
// hourCycle 'h23': com hour12:false alguns motores escrevem meia-noite como "24".
const FUSO = 'America/Sao_Paulo';
const fmtDia = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit',
});
const fmtHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO, day: '2-digit', month: '2-digit', hour: '2-digit', hourCycle: 'h23',
});
const fmtDataHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

const partes = (fmt: Intl.DateTimeFormat, d: Date): Record<string, string> =>
  Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));

/** 'YYYY-MM-DD' do dia em Sao Paulo (o que o backend entende por `inicio`/`fim`). */
export const diaSp = (d: Date): string => {
  const p = partes(fmtDia, d);
  return `${p.year}-${p.month}-${p.day}`;
};

/** Default do backend: ontem..hoje em Sao Paulo. */
export const periodoPadrao = (agora: Date = new Date()): { inicio: string; fim: string } => {
  const fim = diaSp(agora);
  const ontem = new Date(`${fim}T12:00:00Z`);
  ontem.setUTCDate(ontem.getUTCDate() - 1);
  return { inicio: ontem.toISOString().slice(0, 10), fim };
};

export const formatarDataHora = (iso: string | null | undefined): string => {
  if (!iso) return SEM_VALOR;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : fmtDataHora.format(d).replace(',', '');
};

/** Rotulo curto do eixo por hora: '22/10 13h'. */
export const formatarHora = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = partes(fmtHora, d);
  return `${p.day}/${p.month} ${p.hour}h`;
};

const HORA_MS = 3_600_000;

/**
 * O backend so devolve horas com requisicao. Sem completar, a madrugada some do
 * eixo e 18h encosta em 6h (a linha do p95 "sobe" num salto que nao existiu).
 * Hora preenchida: zero requisicao e percentil null (a linha quebra, nao cai a 0).
 * Hora do backend com 0 requisicoes (filtro de origem; o pico e de todas): idem.
 */
export const completarHoras = (dados: ResumoHora[]): ResumoHora[] => {
  const ordenadas = dados
    .filter((h) => !Number.isNaN(Date.parse(h.hora)))
    .sort((a, b) => Date.parse(a.hora) - Date.parse(b.hora));
  const saida: ResumoHora[] = [];
  ordenadas.forEach((h, i) => {
    if (i > 0) {
      for (let t = Date.parse(ordenadas[i - 1].hora) + HORA_MS; t < Date.parse(h.hora); t += HORA_MS) {
        saida.push({
          hora: new Date(t).toISOString(),
          requisicoes: 0, app: 0, web: 0, outro: 0,
          p50_ms: null, p95_ms: null, max_ms: null,
          simultaneas_max: 0, db_ms: 0, cpu_ms: 0,
        });
      }
    }
    saida.push(h.requisicoes > 0 ? h : { ...h, p50_ms: null, p95_ms: null, max_ms: null });
  });
  return saida;
};
